/**
 * Volume ladder <-> product form quantity bridge.
 *
 * The ladder does NOT own the add-to-cart. It drives the theme's own quantity input and
 * lets Horizon's product form do the rest, so nothing here has to know how the theme
 * builds its cart request. Coupling is one field: input[name="quantity"].
 *
 * Two-way on purpose: if a shopper bumps the theme's own +/- stepper, the ladder follows.
 * One-way sync leaves the page showing "2 unidades · 70,40 €" while the form holds 5,
 * which is a worse lie than having no ladder at all.
 */
(function () {
  'use strict';

  if (customElements.get('gp-ladder-component')) return;

  class GpLadder extends HTMLElement {
    connectedCallback() {
      // The theme re-renders the product form on variant change, so resolve lazily
      // rather than caching a node that may be replaced underneath us.
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onRowClick);

      const initial = parseInt(this.dataset.preselect, 10);
      if (Number.isFinite(initial) && initial > 1) {
        // Defer: the product form may not be in the DOM yet on first paint.
        requestAnimationFrame(() => this.pushQuantity(initial));
      }

      this.watchQuantityInput();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onRowClick);
      if (this.observer) this.observer.disconnect();
    }

    get form() {
      return this.closest('form[action*="/cart/add"]')
        || document.querySelector('form[action*="/cart/add"]');
    }

    get quantityInput() {
      const form = this.form;
      return form ? form.querySelector('input[name="quantity"]') : null;
    }

    onChange = (event) => {
      const radio = event.target.closest('.gp-ladder__radio');
      if (!radio) return;
      this.pushQuantity(parseInt(radio.value, 10));
      this.paint(parseInt(radio.value, 10));
    };

    // Clicking the label already toggles the radio, but Safari can skip `change` when the
    // click lands on a child span, so mirror the state defensively.
    onRowClick = (event) => {
      const row = event.target.closest('.gp-ladder__row');
      if (!row) return;
      const qty = parseInt(row.dataset.qty, 10);
      if (Number.isFinite(qty)) this.paint(qty);
    };

    paint(qty) {
      this.querySelectorAll('.gp-ladder__row').forEach((row) => {
        row.classList.toggle('is-selected', parseInt(row.dataset.qty, 10) === qty);
      });
    }

    pushQuantity(qty) {
      const input = this.quantityInput;
      if (!input || !Number.isFinite(qty)) return;
      if (parseInt(input.value, 10) === qty) return;

      this.suppress = true;
      input.value = String(qty);
      // Horizon's quantity component listens for both; dispatch both, bubbling, so the
      // web component and any cart-preview listeners stay consistent.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.suppress = false;
    }

    /** Reflect manual edits of the theme's stepper back onto the ladder. */
    watchQuantityInput() {
      const sync = () => {
        if (this.suppress) return;
        const input = this.quantityInput;
        if (!input) return;
        const qty = parseInt(input.value, 10);
        const radio = this.querySelector(`.gp-ladder__radio[value="${qty}"]`);
        if (radio) {
          radio.checked = true;
          this.paint(qty);
        } else {
          // Quantity outside 1-3: no tier is truthful, so show none selected.
          this.querySelectorAll('.gp-ladder__row').forEach((r) => r.classList.remove('is-selected'));
          this.querySelectorAll('.gp-ladder__radio').forEach((r) => { r.checked = false; });
        }
      };

      document.addEventListener('input', (e) => {
        if (e.target && e.target.name === 'quantity') sync();
      });
      document.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'quantity') sync();
      });
    }
  }

  customElements.define('gp-ladder-component', GpLadder);
})();
